// Multi-product SaaS Portfolio Configuration
// Each product has its own pricing and features

export type ProductId = 'soulsafe' | 'soulsafe_leader' | 'berean';

export interface ProductTier {
  name: string;
  priceId: string;
  productId: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
}

export interface Product {
  id: ProductId;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  route: string;
  dashboardRoute: string;
  color: string;
  tiers: ProductTier[];
  freeFeatures: string[];
}

export const PRODUCTS: Record<ProductId, Product> = {
  soulsafe: {
    id: 'soulsafe',
    name: 'Soul Safe Sanctuary™',
    tagline: 'Spiritual Abuse Recovery',
    description: 'AI-powered healing journey for spiritual abuse survivors with courses, counseling, and community.',
    icon: 'GraduationCap',
    route: '/soulsafe',
    dashboardRoute: '/dashboard',
    color: 'hsl(var(--primary))',
    freeFeatures: ['Basic assessment', 'Crisis resources', 'Community forum (read-only)'],
    tiers: [
      {
        name: 'Monthly',
        priceId: 'price_1SiSF3ChUH6OLtXGoPDqK3Kc',
        productId: 'prod_TfnqPj3IrGKJKq',
        price: 2.99,
        interval: 'month',
        features: [
          'Soul Safe Counsel',
          'All 19 Healing Modules',
          'Private Encrypted Journal',
          'Community Support Forum',
          'Progress Tracking & Badges',
           'Soul Safe Sanctuary™ Certificate',
        ],
      },
      {
        name: 'Yearly',
        priceId: 'price_1T8t7hChUH6OLtXGmEIAugpv',
        productId: 'prod_U77MpA5CUoxODR',
        price: 29.90,
        interval: 'year',
        features: [
          'Soul Safe Counsel',
          'All 19 Healing Modules',
          'Private Encrypted Journal',
          'Community Support Forum',
          'Progress Tracking & Badges',
          'Soul Safe Sanctuary™ Certificate',
        ],
      },
    ],
  },
  soulsafe_leader: {
    id: 'soulsafe_leader',
    name: 'Trained Soul Safe Sanctuary™ Leader',
    tagline: 'Church & Ministry Leadership',
    description: 'Complete leader training with church health audits, trauma-informed discipleship, and certification.',
    icon: 'Shield',
    route: '/leader',
    dashboardRoute: '/leader/dashboard',
    color: 'hsl(var(--primary))',
    freeFeatures: [],
    tiers: [
      {
        name: 'Monthly',
        priceId: 'price_1T8r96ChUH6OLtXGcdkj9tAO',
        productId: 'prod_U75JAe7Xej2yRJ',
        price: 5.99,
        interval: 'month',
        features: [
          'Everything in Soul Safe Sanctuary™',
          'Leader Training Portal',
          'Church Health Audit Tools',
          'Trauma-Informed Discipleship Course',
          'Safe Leader Assessment',
          'Trained Soul Safe Sanctuary™ Leader Badge',
        ],
      },
      {
        name: 'Yearly',
        priceId: 'price_1T8QY5ChUH6OLtXGiddACQZg',
        productId: 'prod_U6dptS775mLGVg',
        price: 59.90,
        interval: 'year',
        features: [
          'Everything in Soul Safe Sanctuary™',
          'Leader Training Portal',
          'Church Health Audit Tools',
          'Trauma-Informed Discipleship Course',
          'Safe Leader Assessment',
          'Trained Soul Safe Sanctuary™ Leader Badge',
        ],
      },
    ],
  },
  berean: {
    id: 'berean',
    name: 'Berean Mode + MyScribal',
    tagline: 'AI Bible Study',
    description: 'Deep scripture study with Greek/Hebrew tools, AI commentary, and journaling for spiritual growth.',
    icon: 'Book',
    route: '/berean',
    dashboardRoute: '/berean/dashboard',
    color: 'hsl(280, 60%, 50%)',
    freeFeatures: ['5 daily AI queries', 'Basic word study', 'Read-only notes'],
    tiers: [
      {
        name: 'Pro',
        priceId: 'price_1SirTLChUH6OLtXG2V7CxOQ8',
        productId: 'prod_TgDv0tw56eElAG',
        price: 9.99,
        interval: 'month',
        features: [
          'Unlimited Soul Safe Counsel',
          'Greek/Hebrew Deep Dives',
          'MyScribal Journaling',
          'Cross-Reference Engine',
          'Commentary Integration',
          'Export & Share Notes',
        ],
      },
    ],
  },
};

export const getProductByRoute = (pathname: string): Product | null => {
  // Check for exact matches first
  for (const product of Object.values(PRODUCTS)) {
    if (pathname.startsWith(product.route) || pathname.startsWith(product.dashboardRoute)) {
      return product;
    }
  }
  
  // Default to SoulSafe for legacy routes
  if (pathname === '/dashboard' || pathname === '/courses' || pathname === '/chat' || 
      pathname === '/counsel-bot' || pathname === '/certification' || pathname === '/quiz') {
    return PRODUCTS.soulsafe;
  }
  
  return null;
};

export const getAllProducts = (): Product[] => Object.values(PRODUCTS);
